import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { IconButton, Stack, Typography } from '@mui/material';
import CustomHandle from 'features/co-pilot/campaign-builder/campaign-flow/custom-handle';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edge, NodeProps, Position, getConnectedEdges, useReactFlow, useStore } from 'reactflow';
import useBottomNodeMenuStore from 'stores/bottom-node-menu-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { makeStyles } from 'tss-react/mui';
import { convertToSeconds, findConnectedNodes, splitTimeInSeconds } from 'utils/flowNode.utils';
import ConfirmDeleteNodeDialog from './confirm-delete-node-dialog';

const selector = (s: any) => ({
  nodeInternals: s.nodeInternals,
  edges: s.edges,
});

const WaitingTimeNode = ({ id, data, xPos, yPos }: NodeProps) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const btnRef = useRef(null);

  const { setNodes, setEdges, getNode } = useReactFlow();
  const { nodeInternals, edges } = useStore(selector);

  const handleOpenMenu = useBottomNodeMenuStore((state) => state.handleOpenMenu);

  const { isStartNode, waitTillNext } = data;
  const customWaitTime = splitTimeInSeconds(waitTillNext);
  const position = { x: xPos, y: yPos };
  const hasEdge = !!edges.find((edge: Edge) => edge.source === id);

  const { selectedNode, setSelectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode,
    setSelectedNode: state.setSelectedNode,
  }));
  const selected = selectedNode?.id === id;

  const [confirmDeleteNodeDialog, setConfirmDeleteNodeDialog] = useState<{ open: boolean }>({ open: false });

  const getHeight = () => {
    const currNode = getNode(id);

    if (!_.isNil(currNode) && !_.isNil(currNode.height)) return currNode.height;

    return undefined;
  };

  /**
   * Delete function Node
   */

  const handleOpenConfirmDeleteNodeDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setConfirmDeleteNodeDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseConfirmDeleteNodeDialog = () => {
    setConfirmDeleteNodeDialog({ open: false });
  };

  const handleDeleteNode = () => {
    const nodeToDelete = nodeInternals.get(id);

    if (!nodeToDelete) return;

    const nodeHeight = (getHeight() || 225) + 84;
    const connectedEdges = getConnectedEdges([nodeToDelete], edges);

    if (connectedEdges.length === 2 && connectedEdges[0].target !== connectedEdges[1].target) {
      const targetEdge = connectedEdges.find((edge) => edge.source === id);
      const sourceEdge = connectedEdges.find((edge) => edge.target === id);

      if (targetEdge && sourceEdge) {
        const updatedEdge = {
          ...sourceEdge,
          target: targetEdge.target,
        };

        setEdges((currentEdges) => currentEdges.filter((edge) => edge.id !== targetEdge.id && edge.id !== sourceEdge.id).concat(updatedEdge));
      }
    } else {
      const connectedEdgeIds = connectedEdges.map((edge) => edge.id);
      setEdges((currentEdges) => currentEdges.filter((edge) => !connectedEdgeIds.includes(edge.id)));
    }

    if (selectedNode?.id === id) {
      setSelectedNode(null);
    }

    setNodes((currentNodes) => {
      const downstreamNodes = findConnectedNodes(id, edges).filter((nodeId) => nodeId !== id);
      return currentNodes
        .filter((node) => node.id !== id)
        .map((node) => {
          if (downstreamNodes.includes(node.id)) {
            return {
              ...node,
              position: {
                ...node.position,
                y: node.position.y - nodeHeight,
              },
            };
          }
          return node;
        });
    });
  };

  /**
   * Node data handlers
   */

  const handleFunctionWaitTimeChange = (type: 'days' | 'hours' | 'minutes') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setNodes((nodes) => {
        const updateNodes = _.cloneDeep(nodes);
        const foundNode = updateNodes.find((node) => node.id === id);

        if (!_.isNil(foundNode)) {
          const updateCustomWaitTime = _.cloneDeep(customWaitTime);
          updateCustomWaitTime[type] = +event.target.value;
          foundNode.data.waitTillNext = convertToSeconds(updateCustomWaitTime);
        }

        return updateNodes;
      });
    };
  };

  return (
    <>
      {!isStartNode ? <CustomHandle className={cx(classes.basicHandle, 'top')} type={'target'} position={Position.Top} isConnectable={1} /> : null}
      <Stack className={cx(classes.node, selected ? 'selected' : undefined)}>
        <IconButton className={cx(classes.closeBtn, 'nodrag', 'nopan')} size={'small'} onClick={handleOpenConfirmDeleteNodeDialog}>
          <CloseRoundedIcon className={classes.closeIcon} />
        </IconButton>
        <Stack
          className={cx(classes.nodeHead, selected ? 'selected' : undefined)}
          direction={'row'}
          alignItems={'center'}
          gap={2}
          onClick={() => setSelectedNode(getNode(id) || null)}
        >
          <span className={classes.nodeNumber}>
            <WatchLaterIcon className={classes.nodeHeadIcon} />
          </span>
          <Typography className={classes.nodeHeadText}>{t('waitingTime')}</Typography>
        </Stack>
        <Stack className={cx(classes.nodeContent, 'nodrag', 'nopan')} gap={2}>
          <Stack direction={'row'} gap={2}>
            <PaiTextField
              value={customWaitTime?.days || 0}
              label={t('days')}
              type={'number'}
              variant={'background'}
              onChange={handleFunctionWaitTimeChange('days')}
            />
            <PaiTextField
              value={customWaitTime?.hours || 0}
              label={t('hours')}
              type={'number'}
              variant={'background'}
              onChange={handleFunctionWaitTimeChange('hours')}
            />
            <PaiTextField
              value={customWaitTime?.minutes || 0}
              label={t('minutes')}
              type={'number'}
              variant={'background'}
              onChange={handleFunctionWaitTimeChange('minutes')}
            />
          </Stack>
        </Stack>
      </Stack>
      <CustomHandle className={cx(classes.basicHandle, 'bottom')} type={'source'} position={Position.Bottom} isConnectable={1}>
        {!hasEdge ? (
          <PaiIconButton
            ref={btnRef}
            sx={{ position: 'absolute', left: 'calc((40px - 16px + 2px) / -2)', bottom: '-56px' }}
            onClick={() => handleOpenMenu(btnRef.current, id, position, getHeight())()}
          >
            <AddRoundedIcon />
          </PaiIconButton>
        ) : null}
      </CustomHandle>

      <ConfirmDeleteNodeDialog
        open={confirmDeleteNodeDialog.open}
        handleClose={handleCloseConfirmDeleteNodeDialog}
        handleDeleteNode={handleDeleteNode}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  node: {
    position: 'relative',
    background: '#FFF',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    width: '320px',
    minHeight: '100px',
    '&.selected': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  basicHandle: {
    background: `${theme.palette.greys.medium}`,
    borderRadius: '4px',
    zIndex: 99,
    width: '16px',
    height: '16px',
    '&.top': {
      top: '-8px',
    },
    '&.bottom': {
      bottom: '-8px',
    },
  },
  nodeHead: {
    padding: '12px 16px',
    borderBottom: '1px solid #F0F0F5',
    cursor: 'pointer',
    '&:hover': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
    },
    '&.selected': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
      '& > span': {
        background: theme.palette.primary.main,
      },
    },
  },
  nodeNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: theme.palette.greys.light,
  },
  nodeHeadText: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  nodeHeadIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: '#FFF',
  },
  closeBtn: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  closeIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  nodeContent: {
    padding: '14px 16px',
  },
}));

export default memo(WaitingTimeNode);
